import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/DeviceStorageFromRepair.css';
import FormContainer from '../CustomElements/FormContainer';
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomContainer from '../CustomElements/CustomContainer';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import useFormDataChanged from '../../customHook/useFormDataChanged';
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useSuccessListener from '../../customHook/useSuccessListener';
// Утилиты
import { getCurrentStatusId } from '../../utils/getCurrentStatusId';
import { handleUniversalChange } from '../../utils/formUtils';
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
// Действия Redux
import { initStatusesFromServer } from '../../redux/actions/statuses.action';
import { transferDeviceForStorage } from '../../redux/actions/devices.action';
import { editStorageStatusFromServer } from '../../redux/actions/statuses.action';
import { load } from '../../redux/actions/loader.action';
import { omitFields } from '../../utils/omitFields';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';

// компонент для изменения статуса возврат из ремонта и передачи устройства на хранение

function TransferDeviceToStorageFromRepair({ currentDevice, closeModal, currentStatus, title, isEdit, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // получаем опции из стора
  const optionsForSelect = useSelector((state) => state.options);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  // отмена подтверждения
  const closeStatusEditModal = () => {
    setAddSuccess(false);
  };

  // Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () =>
      currentStatus
        ? {
            id: currentStatus?.id,
            deviceId: currentStatus?.deviceId,
            fromStatusId: currentStatus?.fromStatusId,
            note: currentStatus?.note || '',
            isBroken: currentStatus?.broken,
            typeDevice: currentDevice?.typeId || '',
            date: convertToDate(currentStatus?.date) || '',
            storageId: currentStatus?.storageId || '',
            receiverId: currentStatus?.receiverId || '',
            acceptanceCert: currentStatus?.acceptanceCert || '',
            acceptanceCertName: currentStatus?.acceptanceCertName || '',
            attachment: currentStatus?.attachment || '',
            attachmentName: currentStatus?.attachmentName || '',
          }
        : {
            deviceId: currentDevice.id,
            fromStatusId: getCurrentStatusId(currentDevice?.status, optionsForSelect.deviceStatuses),
            date: '',
            acceptanceCert: '',
            receiverId: '',
            storageId: '',
            isBroken: '',
            attachment: '',
            note: '',
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus, currentDevice]
  );
  const [formData, setFormData] = useState(initialFormData);

  // Обновляем formData при изменении currentStatus
  useEffect(() => {
    setFormData(initialFormData);
    // Установить флаг открытия формы в true
  }, [initialFormData]);

  //проверяем были ли изменения в форме (true-да, true-нет)
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEdit);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, []);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    // При редактировании — открываем модалку подтверждения
    if (isEdit) {
      setAddSuccess(true);
      return;
    }
    //переводим в состояние загрузки
    dispatch(load());
    // Удаляем лишние поля, не подлежащие отправке
    const formWithoutExcludedFields = omitFields(formData, ['deviceId', 'acceptanceCertName', 'attachmentName']);
    // Удаляем поля с пустыми строками
    const formWithoutEmptyStrings = removeEmptyStrings(formWithoutExcludedFields);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutEmptyStrings, ['date']);
    // Отправка данных
    dispatch(
      transferDeviceForStorage({
        payload,
        deviceId: formData?.deviceId,
        type: currentDevice?.typeId,
        path: 'repair',
      })
    );
  };

  const handlerSubmitEdit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcludedFields = omitFields(formDataWithEmptyStrings, [
      'id',
      'deviceId',
      'acceptanceCertName',
      'attachmentName',
    ]);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutExcludedFields, ['date']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editStorageStatusFromServer({
        payload,
        deviceId: formData?.deviceId,
        statusId: formData?.id,
        type: currentDevice?.typeId,
        path: 'repair',
      })
    );
  };

  //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal, closeStatusEditModal);

  // при размонтировании отправляем запрос чтобы получить обновленный стейт
  useEffect(() => {
    return () => {
      dispatch(initStatusesFromServer(currentDevice.id, 'repair'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CustomContainer>
      <CustomTitle
        titleText={title || t?.titleAcceptForStorage || 'ПРИНЯТИЕ НА ХРАНЕНИЕ УСТРОЙСТВА'}
        deviceType={currentDevice.type}
        deviceId={currentDevice.id}
      />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.statusChangeConfirmationText || 'Вы уверены, что хотите изменить данные статуса?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmitEdit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={t?.repairAcceptanceDateLabel || 'Дата приемки из ремонта *'}
            placeholder={t?.placeholderSelectDate || 'Выберите дату'}
            selectedDate={formData.date}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
            name="date"
            minDate={convertToDate(currentDevice?.lastActionDate)}
            maxDate={new Date()}
          />
          <Selector
            column="left"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'receiverId', selectedOption, 'select')
            }
            label={t?.repairReceiverNameLabel || 'ФИО принявшего из ремонта *'}
            placeholder={t?.placeholderSelectEmployee || 'Выберите ФИО'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect.employees?.find((option) => option.value === formData.receiverId)}
            isMulti={false}
            name="receiverId"
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            rowSpan={2}
          />
          <Selector
            column="right"
            options={optionsForSelect.storages}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'storageId', selectedOption, 'select')
            }
            label={t?.labelStorageLocation || 'Место хранения *'}
            placeholder={t?.placeholderStorageLocation || 'Выберите место хранения'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect.storages?.find((option) => option.value === formData.storageId)}
            isMulti={false}
            name="storageId"
          />
          <Selector
            column="right"
            options={optionsForSelect.condition.filter((item) => item.value !== undefined)}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select');
            }}
            label={t?.labelCondition || 'Состояние *'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={optionsForSelect.condition?.find((option) => option.value === formData.isBroken)}
            isMulti={false}
            name="isBroken"
          />
          <FilePicker
            column="right"
            label={`${t?.serviceReportTitle} *` || 'Акт выполненных работ *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="attachmentName"
            fieldIdName="attachment"
            rowSpan={2}
          />
          <CustomTextarea
            type="textArea"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="note"
            label={t?.labelComment || 'Комментарий *'}
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            width="100%"
            height="var(--sadr-font-size-200)"
            value={formData.note}
            columnspan={2}
          />
          <CustomButton
            onClick={(event) => {
              handlerSubmit(event);
            }}
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={
              isEdit ? t?.buttonSaveChanges || 'Сохранить изменения' : t?.buttonAcceptDevice || 'Принять устройство'
            }
            disabled={isButtonDisabled || !isFormEdit}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default TransferDeviceToStorageFromRepair;
